import { t } from "../../i18n";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatTl, type PlanDto } from "@tasduvar/shared";
import { api } from "../../api/client";
import { LANDING_WRAP, LandingShell, ProofList } from "../../ui/LandingShell";

const BASE = (import.meta.env.BASE_URL as string) || "/";
const img = (name: string): string => `${BASE}img/${name}`;

const FEATURES: [string, string, string][] = [
  ["01", "Üç duvar tipi", "Taş (kargir) ağırlık, betonarme konsol (T/L) ve payandalı duvar; betonarmede TS 500 / EN 1992-1-1 / ACI 318-19 ile kesit tasarımı ve donatı planı."],
  ["02", "Üç yönetmelik, tek girdi", "TBDY 2018 γR'li doğrulama, Eurocode 7 kısmi katsayı kombinasyonları (DA1/DA2/DA3) ve ABD ASD aynı duvar üzerinde."],
  ["03", "Depremli hesap açık hesapla", "Mononobe-Okabe ile statik + dinamik itki, duvar ve kama ataleti; her adımın formülü ve yerine konmuş sayıları raporda."],
  ["04", "CAD gibi çalışır", "Şerit, komut satırı, katman yöneticisi; ölçüleri çizim üzerinde düzenleyin, çokgen kesit çizin ya da DXF'ten alın."],
  ["05", "Metraj ve keşif", "Kazı, dolgu, taş/beton, kalıp, donatı ve drenaj metrajı; ÇŞB 2026 poz numaralı, düzenlenebilir birim fiyatlar."],
  ["06", "Zemin raporundan al", "Zemin etüt raporunu (PDF) yükleyin; tabaka parametreleri ve deprem katsayıları sayfa referansıyla önerilir, siz onaylarsınız."],
];

const STEPS: { n: string; title: string; text: string; img: string; alt: string }[] = [
  { n: "1", title: "Kesiti girin", text: "Duvar tipini seçin, ölçüleri çizim üzerinde düzenleyin; zemin, su, yük ve deprem sekmelerini doldurun.", img: "ekran-kesit.jpg", alt: "Koyu model alanında ölçülü kesit ve sonuç paleti" },
  { n: "2", title: "Hükümleri anında görün", text: "Devrilme, kayma, taşıma gücü, dış merkezlilik, toptan göçme; betonarmede donatı. Her değişiklikte kesit ve 3B yenilenir.", img: "ekran-3b.jpg", alt: "3B görünüm: duvar, dolgu ve kuvvet okları" },
  { n: "3", title: "Raporu teslim edin", text: "Kapak, girdiler, ölçülü kesit, adım adım hesap, donatı, metraj ve keşif. PDF, Word, DXF ve XLSX.", img: "ekran-rapor.png", alt: "Hesap raporu kapağı" },
];

/** Misafir ana sayfası (API'li sürüm): /giris ile aynı kabuk; uygulama yalnız üyelere açık — burada proje açma/örnek yok. */
export function GuestHome(): JSX.Element {
  const [plans, setPlans] = useState<PlanDto[] | null>(null);
  useEffect(() => { void api<PlanDto[]>("GET", "/billing/plans").then((p) => setPlans(p.filter((x) => x.priceKurus > 0))).catch(() => setPlans(null)); }, []);
  return (
    <LandingShell actions={<>
      <Link to="/giris" className="hover:text-[#16233B]">{t("Giriş yap")}</Link>
      <Link to="/kayit" className="lp-cta !px-[12px] !py-[6px] !text-[13.5px] max-sm:hidden">{t("Hesap oluştur")}</Link>
    </>}>
      <main className="flex-1">
        <section className={`${LANDING_WRAP} grid items-center gap-[clamp(32px,5vw,64px)] py-[clamp(40px,7vw,88px)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]`}>
          <div className="max-w-[540px]">
            <p className="font-mono text-[12px] tracking-[0.06em] uppercase text-[#5A6068]">{t("TBDY 2018 · Eurocode 7 · TS 7994")}</p>
            <h1 className="mt-[14px] text-[clamp(32px,3.6vw,46px)] leading-[1.1] font-semibold tracking-[-0.03em]">{t("İstinat duvarı hesabı, kesitten rapora.")}</h1>
            <p className="mt-[18px] text-[16px] leading-[1.6] text-[#3A3F45]">{t("Taş ağırlık, betonarme konsol ve payandalı duvarlar için devrilme, kayma, taşıma gücü ve kesit tasarımı; metraj, keşif ve kurumlara sunulabilir rapor. Girdiyi değiştirdiğiniz anda kesit ve hükümler yenilenir.")}</p>
            <div className="mt-[28px] flex flex-wrap gap-[10px]">
              <Link to="/kayit" className="lp-cta">{t("Hesap oluştur")}</Link>
              <Link to="/giris" className="lp-cta-ghost">{t("Giriş yap")}</Link>
            </div>
            <p className="mt-[14px] text-[13.5px] text-[#5A6068]">{t("Projeleriniz hesabınızda saklanır; her cihazdan açılır.")}</p>
          </div>
          <figure className="lp-shot">
            <img src={img("ekran-proje.jpg")} alt={t("TaşDuvar CAD ekranı: şerit, özellikler paleti, koyu model alanında kesit ve sonuçlar")} width={2160} height={1350} className="w-full h-auto block" />
          </figure>
        </section>

        <section className="border-y border-rule bg-sheet" aria-labelledby="gh-ne">
          <div className={`${LANDING_WRAP} py-[clamp(40px,6vw,72px)]`}>
            <h2 id="gh-ne" className="lp-h2">{t("Ne yapar?")}</h2>
            <div className="mt-[24px] grid gap-[16px] md:grid-cols-2">
              <ProofList items={FEATURES.slice(0, 3)} />
              <ProofList items={FEATURES.slice(3)} />
            </div>
          </div>
        </section>

        <section className={`${LANDING_WRAP} py-[clamp(40px,6vw,72px)]`} aria-labelledby="gh-nasil">
          <h2 id="gh-nasil" className="lp-h2">{t("Nasıl çalışır?")}</h2>
          <ol className="mt-[24px] grid gap-[24px] md:grid-cols-3">
            {STEPS.map((s) => (
              <li key={s.n} className="grid gap-[12px] content-start">
                <figure className="lp-shot aspect-[4/3] bg-[#1B1E22]"><img src={img(s.img)} alt={t(s.alt)} loading="lazy" className="w-full h-full object-cover object-top block" /></figure>
                <div>
                  <div className="font-mono text-[12px] text-[#5A6068]">{s.n} / 3</div>
                  <h3 className="mt-[2px] text-[16px] font-semibold">{t(s.title)}</h3>
                  <p className="mt-[4px] text-[14px] leading-[1.55] text-[#3A3F45]">{t(s.text)}</p>
                </div>
              </li>
            ))}
          </ol>
          <figure className="lp-shot mt-[32px]">
            <img src={img("ekran-betonarme.jpg")} alt={t("Payandalı betonarme duvar: donatı, kesit tasarımı tablosu ve sonuçlar")} loading="lazy" width={2160} height={1350} className="w-full h-auto block" />
            <figcaption className="px-[16px] py-[10px] border-t border-rule bg-sheet text-[13px] text-[#5A6068]">{t("Payandalı betonarme duvarda donatı, kesit tasarımı (TS 500 / EC2 / ACI 318) ve sonuç paleti.")}</figcaption>
          </figure>
        </section>

        <section className="border-t border-rule bg-sheet" aria-labelledby="gh-plan">
          <div className={`${LANDING_WRAP} py-[clamp(40px,6vw,72px)] grid gap-[32px] md:grid-cols-2 items-start`}>
            <div>
              <h2 id="gh-plan" className="lp-h2">{t("Planlar")}</h2>
              <p className="mt-[12px] text-[15px] leading-[1.6] text-[#3A3F45]">{t("Aylık ya da yıllık abonelik; havale/EFT ya da kartla ödeme. Abonelik bitince projeleriniz silinmez, salt okunur kalır.")}</p>
              <div className="mt-[20px] flex flex-wrap gap-[10px]"><Link to="/kayit" className="lp-cta">{t("Hesap oluştur")}</Link><Link to="/planlar" className="lp-cta-ghost">{t("Planları incele")}</Link></div>
            </div>
            <div className="grid gap-[12px] sm:grid-cols-2">
              {(plans ?? []).map((p) => (
                <div key={p.code} className="border border-rule rounded-lg bg-sheet p-[18px]">
                  <div className="text-[14px] font-semibold">{p.name}</div>
                  <div className="mt-[4px] text-[26px] font-semibold tabular-nums">{formatTl(p.priceKurus)} <span className="text-[13px] text-[#5A6068] font-normal">/ {p.days === 365 ? t("yıl") : t("ay")}</span></div>
                  <div className="mt-[2px] text-[12.5px] text-[#5A6068]">{t("KDV dâhil")}{p.days === 365 ? ` · ${t("2 ay bedava")}` : ""}</div>
                </div>
              ))}
              {plans && plans.length === 0 ? <p className="text-[14px] text-[#5A6068]">{t("Fiyatlar yakında.")}</p> : null}
            </div>
          </div>
        </section>
      </main>
    </LandingShell>
  );
}
